import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams, Navigate } from 'react-router-dom';
import { FaCheckCircle, FaBolt, FaCalendarCheck, FaUserCheck, FaStar, FaDollarSign, FaMapMarkerAlt, FaFire, FaUsers } from 'react-icons/fa';
import { trackEvent } from '../utils/analytics';
import api from '../services/api';
import SEOHead from '../components/SEOHead';
import { CITIES, CITY_SLUGS } from './CityGigsPage';

const CityWorkerLandingPage: React.FC = () => {
  const { city } = useParams<{ city: string }>();
  const [searchParams] = useSearchParams();
  const utmSource = searchParams.get('utm_source') || '';
  const [liveJobCount, setLiveJobCount] = useState<number | null>(null);

  const data = city ? CITIES[city] : null;

  const registerUrl = utmSource
    ? `/register?role=worker&${searchParams.toString()}`
    : '/register?role=worker';

  useEffect(() => {
    if (utmSource) {
      trackEvent('utm_landing', 'acquisition', `${utmSource}/${searchParams.get('utm_medium') || ''}/${searchParams.get('utm_campaign') || ''}/${searchParams.get('utm_content') || ''}`);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!data) return;
    let isActive = true;
    const locationParam = data.metro
      ? `metro=${encodeURIComponent(data.metro)}`
      : `location=${encodeURIComponent(data.searchLocation || data.name)}`;
    api.get(`/scraped-jobs?${locationParam}&limit=1`)
      .then((res: any) => {
        if (isActive) setLiveJobCount(res.data?.total ?? 0);
      })
      .catch(() => {});
    return () => {
      isActive = false;
    };
  }, [data?.name]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!city || !CITIES[city]) {
    return <Navigate to="/for-workers" replace />;
  }

  const pageTitle = `Find AV & Stagehand Gigs in ${data!.name} | StageCrew`;
  const pageUrl = `https://stagecrew.app/for-workers/${data!.slug}`;

  return (
    <div>
      <SEOHead
        title={pageTitle}
        description={`Get matched with AV technician, stagehand, and live event gigs in ${data!.name}, ${data!.stateAbbr}. Browse sourced listings and save your work preferences.`}
        url={pageUrl}
        keywords={`stagehand jobs ${data!.name}, AV technician gigs ${data!.name}, freelance stagehand ${data!.name}, live event crew ${data!.name}, ${data!.name} stagehand work near me`}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: pageTitle,
          url: pageUrl,
          description: `Find AV technician and stagehand gigs in ${data!.name}, ${data!.stateAbbr}. Browse sourced listings and save your work preferences.`,
          mainEntity: {
            '@type': 'Service',
            name: `StageCrew ${data!.name}`,
            serviceType: 'Job Matching for Live Events Crew',
            areaServed: {
              '@type': 'City',
              name: data!.name,
              containedInPlace: { '@type': 'State', name: data!.state },
            },
            provider: {
              '@type': 'Organization',
              name: 'StageCrew',
              url: 'https://stagecrew.app',
            },
          },
        }}
      />

      {/* Hero Section — city-specific */}
      <section className="bg-gradient-to-br from-emerald-700 via-teal-600 to-teal-700 text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-4">
              <FaMapMarkerAlt className="text-yellow-400 text-xl" />
              <span className="text-yellow-300 font-semibold text-lg">{data!.name}, {data!.stateAbbr}</span>
            </div>
            {liveJobCount !== null && liveJobCount > 0 && (
              <div className="inline-flex items-center gap-2 bg-yellow-400 text-yellow-900 text-sm font-bold px-4 py-1.5 rounded-full mb-4 animate-pulse">
                <FaFire /> {liveJobCount.toLocaleString()} sourced listings currently shown in {data!.name}
              </div>
            )}
            <div className="inline-block bg-white bg-opacity-20 text-sm font-bold px-4 py-1.5 rounded-full mb-6">
              Free to Browse — No Card Required
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              Find AV &amp; Stagehand Gigs in {data!.name}
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-teal-100 max-w-xl">
              Browse sourced AV, stagehand, and live-event listings in {data!.name}. Job volume and requirements change as sources update.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to={registerUrl}
                className="bg-white text-teal-700 px-10 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-lg text-center"
              >
                Find Gigs in {data!.name}
              </Link>
              <a
                href="#how-it-works"
                className="bg-transparent border-2 border-white text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-white hover:bg-opacity-10 transition text-center"
              >
                See How It Works
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar — city-specific */}
      <section className="bg-white border-b border-gray-100 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-teal-600">{liveJobCount ?? '—'}</div>
              <div className="text-gray-600 text-sm mt-1">Active {data!.name} Jobs</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-teal-600">{data!.venues.length}</div>
              <div className="text-gray-600 text-sm mt-1">Major Venues</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-teal-600">$0</div>
              <div className="text-gray-600 text-sm mt-1">Platform Fees</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-teal-600">In App</div>
              <div className="text-gray-600 text-sm mt-1">Match Updates</div>
            </div>
          </div>
        </div>
      </section>

      {/* Example venues + roles */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Example Venues in {data!.name}</h2>
              <div className="space-y-3">
                {data!.venues.map((venue) => (
                  <div key={venue} className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm">
                    <FaStar className="text-yellow-500" />
                    <span className="font-medium text-gray-800">{venue}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Crew Roles to Search</h2>
              <div className="space-y-3">
                {data!.topRoles.map((role) => (
                  <div key={role} className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm">
                    <FaUsers className="text-teal-500" />
                    <span className="font-medium text-gray-800">{role}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Local Insight */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-2xl p-8 md:p-12 border border-teal-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              <FaBolt className="inline text-yellow-500 mr-2" />
              Review Each {data!.name} Listing
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              Listing volume changes over time. Check the original source, work date, location, rate, and application method before you act.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-4">Find Gigs in {data!.name} in 3 Steps</h2>
          <p className="text-center text-gray-600 mb-16 text-lg max-w-2xl mx-auto">
            Save your work preferences, review relevant {data!.name} listings, and apply on your terms.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="bg-teal-100 text-teal-600 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <FaUserCheck className="text-3xl" />
              </div>
              <div className="bg-teal-600 text-white text-sm font-bold px-3 py-1 rounded-full inline-block mb-3">Step 1</div>
              <h3 className="text-xl font-bold mb-3">Build Your Profile</h3>
              <p className="text-gray-600">
                Add the skills, certifications, and {data!.name} availability you want employers to review.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-teal-100 text-teal-600 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <FaBolt className="text-3xl" />
              </div>
              <div className="bg-teal-600 text-white text-sm font-bold px-3 py-1 rounded-full inline-block mb-3">Step 2</div>
              <h3 className="text-xl font-bold mb-3">Review Matches in {data!.name}</h3>
              <p className="text-gray-600">
                StageCrew compares current listings with your worker-provided profile and saved work preferences.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-teal-100 text-teal-600 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <FaCalendarCheck className="text-3xl" />
              </div>
              <div className="bg-teal-600 text-white text-sm font-bold px-3 py-1 rounded-full inline-block mb-3">Step 3</div>
              <h3 className="text-xl font-bold mb-3">Review & Apply</h3>
              <p className="text-gray-600">
                Review the original source and application details, then apply on your terms.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Before vs After */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-4">Stop Chasing Gigs in {data!.name}</h2>
          <p className="text-center text-gray-600 mb-12 text-lg max-w-2xl mx-auto">
            You know the hustle. Here's what changes when you join StageCrew.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white rounded-xl p-8 border border-red-200">
              <h3 className="text-lg font-bold text-red-600 mb-6">Without StageCrew</h3>
              <ul className="space-y-4">
                {[
                  'Texting 20 contacts hoping someone has a call',
                  'Comparing scattered posts with missing pay or unclear status',
                  'Empty weeks followed by double-booked weekends',
                  'No way to prove your track record to new clients',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-gray-600">
                    <span className="text-red-400 mt-0.5 flex-shrink-0">&#10007;</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-xl p-8 border-2 border-teal-500">
              <h3 className="text-lg font-bold text-teal-600 mb-6">With StageCrew in {data!.name}</h3>
              <ul className="space-y-4">
                {[
                  `Current listings near ${data!.venues[0]}, ${data!.venues[1]}, and other local venues when sources publish them`,
                  'Paid StageCrew and supported employer-board listings in one dashboard',
                  'Role, rate, location, and source details to review',
                  'Worker-provided profile details and available reviews',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-gray-700">
                    <FaCheckCircle className="text-teal-500 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why StageCrew */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">Why {data!.name} Crew Choose StageCrew</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-8 rounded-xl">
              <FaCalendarCheck className="text-3xl text-teal-600 mb-4" />
              <h3 className="text-xl font-bold mb-2">Review Relevant Listings</h3>
              <p className="text-gray-600">
                Paid automation can attempt applications using your saved preferences where the posting destination supports submission. Review attempt status before relying on it.
              </p>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl">
              <FaDollarSign className="text-3xl text-teal-600 mb-4" />
              <h3 className="text-xl font-bold mb-2">Your Rate, Your Terms</h3>
              <p className="text-gray-600">
                Save a preferred rate and travel radius to help evaluate listings. Always review the posted rate, location, and source details yourself.
              </p>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl">
              <FaStar className="text-3xl text-teal-600 mb-4" />
              <h3 className="text-xl font-bold mb-2">Build Your Reputation</h3>
              <p className="text-gray-600">
                Keep your worker-provided skills and work history current, and collect reviews when an employer chooses to leave one.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Event Types */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-8">Event Types in {data!.name}</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {data!.eventTypes.map((type) => (
              <span key={type} className="bg-teal-50 text-teal-700 border border-teal-200 px-4 py-2 rounded-full text-sm font-medium">
                {type}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-teal-600 to-emerald-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Work in {data!.name}?
          </h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
            Browse current {data!.name} listings and create a free profile for saved preferences and in-app matches.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to={registerUrl}
              className="bg-white text-teal-600 px-10 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-lg"
            >
              Create Your Free Profile
            </Link>
            <Link
              to={`/gigs/${data!.slug}`}
              className="bg-transparent border-2 border-white text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-white hover:bg-opacity-10 transition"
            >
              Browse {data!.name} Gigs
            </Link>
          </div>
        </div>
      </section>

      {/* Explore Other Cities */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Explore Other Markets</h2>
          <div className="flex flex-wrap gap-3">
            {CITY_SLUGS.filter((s) => s !== city).slice(0, 12).map((slug) => (
              <Link
                key={slug}
                to={`/for-workers/${slug}`}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-teal-100 hover:text-teal-700 transition font-medium text-sm"
              >
                <FaMapMarkerAlt className="inline mr-1 text-xs" />
                {CITIES[slug].name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CityWorkerLandingPage;
